/**
 * #439/#441: one statement policy line -- the broker's decision, the dispute
 * (only the actions the server allows from the current status) and the whole
 * history, dispute events and amount corrections merged by time.
 *
 * A broker admin acts; anyone else (a super admin) reads. The controls are not
 * rendered for them at all.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Badge, Button } from '@/components/common';
import { ReportSideDrawer } from '@/pages/reports/ReportSideDrawer';
import { getErrorMessage } from '@/services';
import { statementVerificationService } from '@/services/statementVerification';
import type {
  DisputeAction,
  LineDecision,
  StatementPolicyLine,
} from '@/services/statementVerification';
import { formatCurrency, formatDateTime } from '@/utils';
import {
  ACTIONS_WITH_AMOUNT,
  ACTION_LABELS,
  CATEGORY_LABELS,
  DECISION_LABELS,
  DISPUTE_LABELS,
  flagLabel,
} from './statementLabels';

interface Props {
  line: StatementPolicyLine | null;
  canAct: boolean;
  onClose: () => void;
}

const money = (v: string | null | undefined) => (v == null || v === '' ? '—' : formatCurrency(v));

export function StatementLineDrawer({ line, canAct, onClose }: Props) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<DisputeAction | null>(null);
  const [note, setNote] = useState('');
  const [share, setShare] = useState(false);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');

  const lineId = line?.id ?? null;
  const disputeId = line?.dispute?.id ?? null;

  const dispute = useQuery({
    queryKey: ['statementDispute', disputeId],
    queryFn: () => statementVerificationService.getDispute(disputeId!),
    enabled: Boolean(disputeId),
  });
  const history = useQuery({
    queryKey: ['statementLineHistory', lineId],
    queryFn: () => statementVerificationService.lineHistory(lineId!),
    enabled: Boolean(lineId),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['statementLines'] });
    qc.invalidateQueries({ queryKey: ['statementDispute'] });
    qc.invalidateQueries({ queryKey: ['statementLineHistory'] });
    qc.invalidateQueries({ queryKey: ['statementDisputeSummary'] });
    qc.invalidateQueries({ queryKey: ['statementBatches'] });
  };

  const decide = useMutation({
    mutationFn: (decision: LineDecision) =>
      statementVerificationService.decide(lineId!, { decision }),
    onSuccess: () => {
      toast.success('Decision saved');
      refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const transition = useMutation({
    mutationFn: () =>
      statementVerificationService.transition(disputeId!, {
        action: pending!,
        note,
        share_with_insurer: share,
        amount_settled: ACTIONS_WITH_AMOUNT.includes(pending!) && amount ? amount : undefined,
        insurer_reference: pending === 'raise' && reference ? reference : undefined,
        from_status: dispute.data?.status,
      }),
    onSuccess: () => {
      toast.success('Dispute updated');
      setPending(null);
      setNote('');
      setShare(false);
      setAmount('');
      setReference('');
      refresh();
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
      refresh();
    },
  });

  if (!line) return null;
  const decisions = (Object.keys(DECISION_LABELS) as Exclude<LineDecision, ''>[]).filter(
    (d) => line.category !== 'not_in_books' || d === 'none' || d === 'dispute',
  );
  const allowed = dispute.data?.allowed_actions ?? [];
  const needsAmount = pending !== null && ACTIONS_WITH_AMOUNT.includes(pending);
  const amountRequired = pending === 'resolve' || pending === 'write_off';
  const canSubmit = note.trim() !== '' && (!amountRequired || amount.trim() !== '');

  return (
    <ReportSideDrawer
      isOpen={Boolean(line)}
      onClose={onClose}
      title={line.base_policy_number}
      subtitle={`${line.insurer_name} · ${CATEGORY_LABELS[line.category]}`}
    >
      <dl className="mb-4 grid grid-cols-3 gap-3 text-sm">
        <div>
          <dt className="text-xs text-gray-500 dark:text-gray-400">Statement</dt>
          <dd className="font-medium text-gray-900 dark:text-gray-100">{money(line.stated_total)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 dark:text-gray-400">Our books</dt>
          <dd className="font-medium text-gray-900 dark:text-gray-100">{money(line.expected_total)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 dark:text-gray-400">Difference</dt>
          <dd className="font-medium text-gray-900 dark:text-gray-100">{money(line.difference)}</dd>
        </div>
      </dl>
      {line.reason && <p className="mb-3 text-sm text-gray-600 dark:text-gray-300">{line.reason}</p>}
      {line.flags.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1">
          {line.flags.map((f) => (
            <Badge key={f} variant="warning" size="sm">
              {flagLabel(f)}
            </Badge>
          ))}
        </div>
      )}

      <section className="mb-5" aria-label="Decision">
        <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Decision</h4>
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          Neither side is assumed right. Current:{' '}
          <span className="font-medium">
            {line.decision ? DECISION_LABELS[line.decision] : DECISION_LABELS.none}
          </span>
        </p>
        {canAct && (
          <div className="flex flex-wrap gap-2">
            {decisions.map((d) => (
              <Button
                key={d}
                size="sm"
                variant={line.decision === d ? 'primary' : 'outline'}
                disabled={decide.isPending || line.decision === d}
                onClick={() => decide.mutate(d)}
              >
                {DECISION_LABELS[d]}
              </Button>
            ))}
          </div>
        )}
      </section>

      {line.dispute && (
        <section className="mb-5" aria-label="Dispute">
          <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Dispute</h4>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <Badge variant="primary" size="sm">
              {DISPUTE_LABELS[line.dispute.status]}
            </Badge>{' '}
            disputed {money(line.dispute.amount_disputed)}
            {line.dispute.amount_settled != null && <>, settled {money(line.dispute.amount_settled)}</>}
          </p>
          {dispute.data?.suggest_resolve && (
            <p className="mt-2 rounded bg-green-50 p-2 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300">
              A later statement now matches. Resolve?
            </p>
          )}
          {canAct && pending === null && (
            <div className="mt-3 flex flex-wrap gap-2">
              {allowed.map((a) => (
                <Button key={a} size="sm" variant="outline" onClick={() => setPending(a)}>
                  {ACTION_LABELS[a]}
                </Button>
              ))}
            </div>
          )}
          {canAct && pending !== null && (
            <form
              className="mt-3 space-y-3 rounded border border-gray-200 p-3 dark:border-gray-700"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSubmit) transition.mutate();
              }}
            >
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{ACTION_LABELS[pending]}</p>
              <label className="block text-sm">
                <span className="text-gray-600 dark:text-gray-300">Note (required)</span>
                <textarea
                  className="mt-1 w-full rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} />
                Share this note with the insurer (included in the export)
              </label>
              {pending === 'raise' && (
                <label className="block text-sm">
                  <span className="text-gray-600 dark:text-gray-300">Insurer reference (optional)</span>
                  <input
                    className="mt-1 w-full rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                    value={reference}
                    maxLength={100}
                    onChange={(e) => setReference(e.target.value)}
                  />
                </label>
              )}
              {needsAmount && (
                <label className="block text-sm">
                  <span className="text-gray-600 dark:text-gray-300">
                    Amount settled{amountRequired ? ' (required)' : ''}
                  </span>
                  <input
                    inputMode="decimal"
                    className="mt-1 w-full rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </label>
              )}
              <div className="flex gap-2">
                <Button size="sm" type="submit" disabled={!canSubmit || transition.isPending}>
                  Save
                </Button>
                <Button size="sm" type="button" variant="ghost" onClick={() => setPending(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </section>
      )}

      <section aria-label="History">
        <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">History</h4>
        {history.isLoading ? (
          <p className="text-sm text-gray-500">Loading history…</p>
        ) : history.isError ? (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            Could not load the history of this line. {getErrorMessage(history.error)}{' '}
            <button type="button" className="underline" onClick={() => history.refetch()}>
              Try again
            </button>
          </div>
        ) : (history.data ?? []).length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nothing has happened to this line yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {history.data!.map((h, i) => (
              <li key={i} className="py-2 text-sm">
                <p className="text-gray-900 dark:text-gray-100">
                  {h.type === 'revision'
                    ? `Amount corrected ${money(h.from)} to ${money(h.to)}`
                    : h.type === 'decision'
                      ? `Decision: ${DECISION_LABELS[h.to as Exclude<LineDecision, ''>] ?? h.to}`
                      : `${DISPUTE_LABELS[h.from as keyof typeof DISPUTE_LABELS] ?? h.from} to ${DISPUTE_LABELS[h.to as keyof typeof DISPUTE_LABELS] ?? h.to}`}
                </p>
                {h.note && <p className="text-gray-600 dark:text-gray-300">{h.note}</p>}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDateTime(h.at)}
                  {h.by ? ` · ${h.by}` : ''}
                  {h.share_with_insurer ? ' · shared with insurer' : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </ReportSideDrawer>
  );
}
